/**
 * Custom hook for handling grid horizontal scroll with sticky columns
 * Manages shadow visibility on sticky columns when grid is scrolled
 */

import { useCallback, useEffect, type UIEvent } from "react";

interface UseGridScrollOptions {
    /** CSS class name for the scroll wrapper element */
    wrapperClass: string;
    /** Whether to track left scroll (for right shadow) */
    trackLeftScroll?: boolean;
    /** Whether to track right scroll (for left shadow) */
    trackRightScroll?: boolean;
    /** Whether grid has data loaded */
    hasData: boolean;
    /** Whether data is currently loading */
    isLoading: boolean;
    /** Additional dependencies to trigger re-check */
    dependencies?: unknown[];
}

interface UseGridScrollReturn {
    /** Scroll event handler to attach to wrapper element */
    handleScroll: (e: UIEvent<HTMLDivElement>) => void;
}

/**
 * Hook to manage sticky column shadows based on horizontal scroll position
 *
 * @param options - Configuration options for scroll behavior
 * @returns Scroll handler and utilities
 *
 * @example
 * ```tsx
 * const { handleScroll } = useGridScroll({
 *     wrapperClass: "my-grid-wrapper",
 *     trackLeftScroll: true,
 *     trackRightScroll: true,
 *     hasData: data.length > 0,
 *     isLoading: false
 * });
 *
 * return <div className="my-grid-wrapper" onScroll={handleScroll}>...</div>
 * ```
 */
export function useGridScroll({
    wrapperClass,
    trackLeftScroll = true,
    trackRightScroll = true,
    hasData,
    isLoading,
    dependencies = [],
}: UseGridScrollOptions): UseGridScrollReturn {
    /**
     * Handles horizontal scroll to toggle shadow visibility on sticky columns
     * Uses requestAnimationFrame for performance optimization
     */
    const handleScroll = useCallback(
        (e: UIEvent<HTMLDivElement>) => {
            const target = e.currentTarget;
            requestAnimationFrame(() => {
                const scrollLeft = target.scrollLeft;
                const scrollWidth = target.scrollWidth;
                const clientWidth = target.clientWidth;

                // Add class when scrolled right (show left shadow)
                if (trackRightScroll) {
                    if (scrollLeft > 0) {
                        target.classList.add("scrolled-right");
                    } else {
                        target.classList.remove("scrolled-right");
                    }
                }

                // Add class when scrolled left (show right shadow)
                if (trackLeftScroll) {
                    if (scrollLeft + clientWidth < scrollWidth - 1) {
                        target.classList.add("scrolled-left");
                    } else {
                        target.classList.remove("scrolled-left");
                    }
                }
            });
        },
        [trackLeftScroll, trackRightScroll]
    );

    /**
     * Check scroll state on initial load to show shadows if columns are floating
     * This handles the case where content is wider than viewport on page load
     */
    useEffect(() => {
        if (hasData && !isLoading) {
            const wrapper = document.querySelector(`.${wrapperClass}`);

            if (wrapper) {
                const scrollLeft = wrapper.scrollLeft;
                const scrollWidth = wrapper.scrollWidth;
                const clientWidth = wrapper.clientWidth;

                // Check if right columns are floating (content extends beyond viewport)
                if (trackLeftScroll && scrollLeft + clientWidth < scrollWidth - 1) {
                    wrapper.classList.add("scrolled-left");
                } else {
                    wrapper.classList.remove("scrolled-left");
                }

                // Check if left columns are floating (already scrolled)
                if (trackRightScroll && scrollLeft > 0) {
                    wrapper.classList.add("scrolled-right");
                } else {
                    wrapper.classList.remove("scrolled-right");
                }
            }
        }
    }, [wrapperClass, trackLeftScroll, trackRightScroll, hasData, isLoading, dependencies]);

    return { handleScroll };
}
